/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import patternsImg from 'assets/images/patterns.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Паттерны</h2>
    <p className={styles.description}>
      В React паттерны различаются по сложности и области применения. От простых, повышающих читабельность, чистоту и
      однородность кода, до архитектурных, позволяющих повторно использовать логику, повышая поддерживаемость кода. Все
      библиотеки в экосистеме React используют несколько архитектурных паттернов.{' '}
    </p>
    <img className={styles.img} src={patternsImg} alt="patterns" />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Higher-Order Component</h3>
      <p className={styles.description}>
        <code className={styles.code}>HOC</code>  — функция, которая принимает компонент как аргумент и возвращает новый
        компонент (функцию или класс) с расширенным функционалом.{' '}
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Применяется когда нужно использовать повторяющуюся логику, применяемую к ряду компонентов, тем самым дополнив
          их функционал.
        </li>
        <li className={styles.list__item}>HOC должен быть чистой функцией без побочных эффектов.</li>
        <li className={styles.list__item}>
          HOC нельзя использовать в методе <code className={styles.code}>render()</code>. Композиция должна быть
          статической, то есть во время экспорта компонента.
        </li>
      </ul>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.1 - Создание и использование</h4>
        <CopyBlock
          text={`// withHigherOrderComponent.js
import React, { Component } from 'react';

const withHigherOrderComponent = WrappedComponent => {
  return class WithHigherOrderComponent extends Component {
    render() {
      return (
        <WrappedComponent {...this.props} extraProp="This prop is from HOC" />
      );
    }
  };
};

export default withHigherOrderComponent;`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Использование компонента высшего порядка называется <b>статическая композиция</b>, потому что создание обертки
          происходит один раз, при экспорте оборачиваемого компонента.{' '}
        </p>
        <CopyBlock
          text={`// MyComponent.js
import React from 'react';
import withHigherOrderComponent from '/path/to/withHigherOrderComponent';

const MyComponent = props => <div>{JSON.stringify(props, null, 2)}</div>;

export default withHigherOrderComponent(MyComponent);`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.2 - withLog</h4>
        <p className={styles.description}>HOC который просто логирует все пропсы полученные компонентом. </p>
        <CopyBlock
          text={`const withLog = WrappedComponent => {
  return class WithLog extends Component {
    componentDidMount() {
      console.group(\`WithLog ouput @$ {WrappedComponent.name}\`);
      console.log(this.props);
      console.groupEnd();
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.3 - withFetch</h4>
        <p className={styles.description}>
          Такой HOC можно использовать в компонентах где необходимо получить данные от сервера.{' '}
        </p>
        <CopyBlock
          text={`// withFetch.js
const withFetch = url => WrappedComponent => {
  return class WithFetch extends Component {
    state = {
      data: [],
      loading: false,
      error: null,
    };

    componentDidMount() {
      this.setState({ loading: true });

      fetch(url)
        .then(res => res.json())
        .then(data => this.setState({ data }))
        .catch(error => this.setState({ error }))
        .finally(() => this.setState({ loading: false }));
    }

    render() {
      return <WrappedComponent {...this.props} {...this.state} />;
    }
  };
};

export default withFetch;`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Теперь используем с любым компонентом, делая код самого компонента чище и убирая потребность в классе.
        </p>
        <CopyBlock
          text={`// App.js
const App = props => {
  return <h1>В моих пропсах будут data, loading и error</h1>;
};

export default withFetch('https://jsonplaceholder.typicode.com/todos')(App);`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.4 - withToggle</h4>
        <p className={styles.description}>HOC позволяющий сделать переключаемым любой компонент. </p>
        <CopyBlock
          text={`const withToggle = WrappedComponent => {
  return class WithToggle extends Component {
    state = {
      isOpen: false,
    };

    toggle = () => this.setState(prevState => ({ isOpen: !prevState.isOpen }));

    render() {
      return (
        <>
          <button type="button" onClick={this.toggle}>
            {this.state.isOpen ? 'Hide' : 'Show'}
          </button>

          {isOpen && <WrappedComponent {...this.props} />}
        </>
      );
    }
  };
};`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Получился HOC, который рендерит кнопку для скрытия или отображения контента. А что если мы хотим другой
          элемент управления? Тогда придется передать состояние <code className={styles.code}>isOpen</code> и метод{' '}
          <code className={styles.code}>toggle</code> пропсами в <code className={styles.code}>WrappedComponent</code>.
          Это загрязняет его пропсы и приводит к проблеме которая называется{' '}
          <code className={styles.code}>prop collision</code>. В случае когда необходимо рендерить разметку, лучше
          использовать паттерн <code className={styles.code}>Render Prop</code>.
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.5 - Дополнительные материалы</h4>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <a
              rel="noopener noreferrer"
              className={styles.link}
              href="https://ru.reactjs.org/docs/higher-order-components.html"
              target="_blank"
            >
              Документация по HOC
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              rel="noopener noreferrer"
              className={styles.link}
              href="https://tylermcginnis.com/react-higher-order-components/"
              target="_blank"
            >
              React Higher-Order Components от Tyler McGinnis
            </a>
          </li>
        </ul>{' '}
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Render Prop</h3>
      <p className={styles.description}>
        Как и HOC, позволяет реализовать повторное использование логики. В большинстве случаев эти паттерны
        взаимозаменяемы. Плюс это отсутствие сайд-эффектов в обёрнутом компоненте. Минус это{' '}
        <a
          className={styles.link}
          href="https://medium.com/@ntgard/why-i-dont-use-render-props-in-react-10f18abdff11"
          target="_blank"
          rel="noopener noreferrer"
        >
          читабельность кода
        </a>{' '}
        при использовании нескольких оберток в одном копоненте. Проблема читабельности в некоторой степени решается
        такими библиотеками как{' '}
        <a
          rel="noopener noreferrer"
          className={styles.link}
          href="https://github.com/pedronauck/react-adopt"
          target="_blank"
        >
          react-adopt
        </a>
        .
      </p>
      <p className={styles.description}>
        Идея использования паттерна заключается в передаче управления рендером другому компоненту, а сам{' '}
        <code className={styles.code}>Render Prop</code> отвечает только за состояние и его обновление. Для этого в проп{' '}
        <code className={styles.code}>children</code> передается функция.
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.1 - Toggler</h4>
        <p className={styles.description}>
          Компонент позволяющий сделать переключаемым любой другой компонент.{' '}
          <code className={styles.code}>{`<Toggler>`}</code> управляет только изменением состояния, а то как это
          состояние будет использовано его не интересует.
        </p>
        <CopyBlock
          text={`// Toggler.js
class Toggler extends Component {
  state = {
    isOpen: false,
  };

  toggle = () => this.setState(state => ({ isOpen: !state.isOpen }));

  render() {
    return this.props.children({
      isOpen: this.state.isOpen,
      onToggle: this.toggle,
    });
  }
}

export default Toggler;`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Использование паттерна <code className={styles.code}>Render Prop</code> это динамическая композиция.{' '}
          <code className={styles.code}>{`<Toggler>`}</code> открывает доступ к состоянию и методу для его изменения.
          Разработчик может использовать одинаковую логику для разных элементов интерфейса.{' '}
        </p>
        <CopyBlock
          text={`// App.js
const App = () => (
  <div>
    <Toggler>
      {({ isOpen, onToggle }) => (
        <>
          <button type="button" onClick={onToggle}>
            {isOpen ? 'Hide' : 'Show'}
          </button>
          {isOpen && <p>Vestibulum suscipit nulla quis orci.</p>}
        </>
      )}
    </Toggler>

    <Toggler>
      {({ isOpen, onToggle }) => (
        <>
          <label>
            <input type="checkbox" checked={isOpen} onChange={onToggle} />
            {isOpen ? 'Hide' : 'Show'}
          </label>
          {isOpen && <p>Etiam feugiat lorem non metus.</p>}
        </>
      )}
    </Toggler>
  </div>
);`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.2 - Autocomplete и TagList</h4>
        <p className={styles.description}>
          Пример повторного использования одной логики фильтрации для создания внешне различных компонентов.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="Render Props example"
          src="https://codesandbox.io/embed/wnvlw82zw8?fontsize=14"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.3 - Дополнительные материалы</h4>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <a rel="noopener noreferrer" className={styles.link} href="https://youtu.be/BcVAq3YFiuc" target="_blank">
              Michael Jackson - Never Write Another HoC
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              rel="noopener noreferrer"
              className={styles.link}
              href="https://tylermcginnis.com/react-render-props/"
              target="_blank"
            >
              React Render Props от Tyler McGinnis
            </a>
          </li>
          <li className={styles.list__item}>
            <a
              className={styles.link}
              href="https://engineering.dollarshaveclub.com/learn-render-props-by-example-da3e2524dd2e"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn Render Props by Example
            </a>
          </li>
        </ul>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a rel="noopener noreferrer" className={styles.link} href="https://youtu.be/0BNgi9vofaw" target="_blank">
            Code Reuse Patterns - Guy Romm
          </a>
        </li>
        <li className={styles.list__item}>
          <a rel="noopener noreferrer" className={styles.link} href="https://react-fns.netlify.com/en/" target="_blank">
            react-fns
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            rel="noopener noreferrer"
            className={styles.link}
            href="https://github.com/acdlite/recompose"
            target="_blank"
          >
            Репозиторий recompose
          </a>
        </li>
      </ul>
    </section>
  </section>
))
